/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SidebarContext.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Context for managing sidebar collapse state between components.

 🔧 Notes:
    - Provides sidebar toggle functionality to Header and Sidebar
    - Manages collapsed state with responsive auto-collapse
    - Auto-collapses sidebar on screens < 1024px (lg breakpoint)
    - Allows Header to toggle sidebar without direct component coupling

 🔁 Last Updated: 2026-01-23 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';

interface SidebarContextType {
  isCollapsed: boolean;
  toggleCollapse: () => void;
  setIsCollapsed: (collapsed: boolean) => void;
  isMobileView: boolean; // Indicates if currently in responsive collapsed mode
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebarContext = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebarContext must be used within a SidebarProvider');
  }
  return context;
};

interface SidebarProviderProps {
  children: ReactNode;
}

// Breakpoint for responsive collapse (Tailwind lg = 1024px)
const MOBILE_BREAKPOINT = 1024;

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const userPreferenceRef = useRef<boolean>(false); // Track user's manual preference

  // Auto-collapse sidebar on mobile/tablet
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobileView(isMobile);

      if (isMobile) {
        // On mobile, always collapse
        setIsCollapsed(true);
      } else {
        // On desktop, restore user preference
        setIsCollapsed(userPreferenceRef.current);
      }
    };

    // Initial check
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    // Save user preference only when not in mobile view
    if (!isMobileView) {
      userPreferenceRef.current = newState;
    }
  };

  const handleSetIsCollapsed = (collapsed: boolean) => {
    setIsCollapsed(collapsed);
    // Save user preference only when not in mobile view
    if (!isMobileView) {
      userPreferenceRef.current = collapsed;
    }
  };

  return (
    <SidebarContext.Provider value={{ isCollapsed, toggleCollapse, setIsCollapsed: handleSetIsCollapsed, isMobileView }}>
      {children}
    </SidebarContext.Provider>
  );
};

